/**
 * ─── Water Station + Jewelry Consignment Pages ──────────────
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { consignmentRepo } from '../../lib/db'
import { CONSIGNMENT_STATUS_LABELS } from '@repo/entity-jewelry'
import { Droplets, Users, Truck, DollarSign, Package, Plus, Handshake, TrendingUp } from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// Water Station Pages
// ═══════════════════════════════════════════════════════════════

export function WaterStationDashboardPage() {
  // Uses existing ws_* repos from db.ts via localStorage bridge
  const [stats, setStats] = useState({ customers: 0, deliveries: 0, containers: 0, payments: 0 })

  useEffect(() => {
    // Mock stats for demo
    setStats({ customers: 12, deliveries: 48, containers: 156, payments: 32 })
  }, [])

  return (
    <div className="p-6">
      <div className="mb-6"><h2 className="text-2xl font-bold text-gray-900">Water Station</h2><p className="text-sm text-gray-500 mt-1">Water refilling station — customers, deliveries, containers, payments</p></div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Customers', stats.customers, 'blue', Users], ['Deliveries', stats.deliveries, 'green', Truck],
          ['Containers', stats.containers, 'purple', Package], ['Payments', stats.payments, 'amber', DollarSign],
        ].map(([label, value, color, Icon]: any) => (
          <div key={label} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <div className="flex items-center gap-2">{Icon && <Icon className={`h-4 w-4 text-${color}-600`} />}<p className={`text-xs text-${color}-600`}>{label}</p></div>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value}</p>
          </div>
        ))}
      </div>
      <Card><CardHeader title="Water Station Overview" description="Customer delivery tracking and container management" />
        <div className="p-6 text-center text-gray-500">
          <Droplets className="h-16 w-16 mx-auto mb-4 text-cyan-300" />
          <p className="text-lg font-medium text-gray-700">Water Station Module Active</p>
          <p className="text-sm mt-2">Ready to manage customers, deliveries, containers, and payments.</p>
        </div>
      </Card>
    </div>
  )
}

export function WaterStationCustomersPage() {
  const [customers, setCustomers] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ name: '', phone: '', address: '' })

  const load = useCallback(async () => {
    try {
      const r = localStorage.getItem('ws_customers')
      setCustomers(r ? JSON.parse(r) : [])
    } catch { setCustomers([]) }
  }, [])
  useEffect(() => { load() }, [load])

  function save() {
    const updated = [{ id: Date.now().toString(), ...form, status: 'active', createdAt: Date.now() }, ...customers]
    setCustomers(updated)
    localStorage.setItem('ws_customers', JSON.stringify(updated))
    setShowModal(false)
    setForm({ name: '', phone: '', address: '' })
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Water Station Customers</h2><p className="text-sm text-gray-500 mt-1">{customers.length} customers</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Customer</Button>
      </div>
      <Card><CardHeader title="Customer List" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Name','Phone','Address','Status'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {customers.length === 0 ? <tr><td colSpan={4} className="py-8 text-center text-gray-400">No customers.</td></tr> :
             customers.map((c: any) => (<tr key={c.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-medium">{c.name}</td><td className="px-3 py-2.5 text-gray-500">{c.phone}</td><td className="px-3 py-2.5 text-xs text-gray-500">{c.address}</td><td className="px-3 py-2.5"><Badge color="green">{c.status ?? 'active'}</Badge></td></tr>))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Customer" size="sm"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} icon={<Users className="h-4 w-4"/>}>Add</Button></div>}>
        <div className="space-y-3">
          <Input label="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
          <Input label="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
          <Input label="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Jewelry Consignment Page
// ═══════════════════════════════════════════════════════════════

export function JewelryConsignmentPage() {
  const [consignments, setConsignments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ consignorName: '', consignorContact: '', consignorAddress: '', commissionPercent: 15, notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await consignmentRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'createdAt', direction: 'desc' }] }); setConsignments('items' in r ? r.items : []) }
    catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      await consignmentRepo.create({ ...form, commissionPercent: Number(form.commissionPercent), status: 'active', agreementDate: Date.now(), totalItemsValue: 0, totalSoldValue: 0, totalCommission: 0, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' } as any)
      setShowModal(false); setForm({ consignorName: '', consignorContact: '', consignorAddress: '', commissionPercent: 15, notes: '' }); await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const totalValue = consignments.reduce((s: number, c: any) => s + (c.totalItemsValue ?? 0), 0)
  const totalCommission = consignments.reduce((s: number, c: any) => s + (c.totalCommission ?? 0), 0)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Consignments</h2><p className="text-sm text-gray-500 mt-1">{consignments.length} agreements · Total Value: ₱{totalValue.toLocaleString()} · Commission: ₱{totalCommission.toLocaleString()}</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>New Consignment</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Active',consignments.filter((c:any)=>c.status==='active').length,'green'],['Total Value',`₱${totalValue.toLocaleString()}`,'blue'],['Total Sold',`₱${consignments.reduce((s:number,c:any)=>s+(c.totalSoldValue??0),0).toLocaleString()}`,'amber'],['Commission',`₱${totalCommission.toLocaleString()}`,'purple']].map(([l,v,c]:any)=>(
          <div key={l} className={`rounded-xl border border-${c}-200 bg-${c}-50 p-4`}><p className={`text-xs text-${c}-600`}>{l}</p><p className={`mt-1 text-xl font-bold text-${c}-700`}>{v}</p></div>
        ))}
      </div>
      <Card><CardHeader title="Consignment Agreements" description="Third-party stock with commission settlement" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Consignor','Contact','Commission %','Items Value','Sold Value','Commission Earned','Status'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             consignments.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">No consignment agreements.</td></tr> :
             consignments.map((c: any) => (
              <tr key={c.id} className="hover:bg-gray-50"><td className="px-3 py-2.5 font-medium">{c.consignorName}</td><td className="px-3 py-2.5 text-xs text-gray-500">{c.consignorContact ?? '—'}</td><td className="px-3 py-2.5 text-center">{c.commissionPercent}%</td><td className="px-3 py-2.5 text-right">₱{(c.totalItemsValue??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right text-green-600">₱{(c.totalSoldValue??0).toLocaleString()}</td><td className="px-3 py-2.5 text-right font-medium">₱{(c.totalCommission??0).toLocaleString()}</td><td className="px-3 py-2.5"><Badge color={c.status==='active'?'green':c.status==='settled'?'blue':'gray'}>{CONSIGNMENT_STATUS_LABELS[c.status as keyof typeof CONSIGNMENT_STATUS_LABELS]??c.status}</Badge></td></tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={() => setShowModal(false)} title="New Consignment Agreement" size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} loading={saving} icon={<Handshake className="h-4 w-4"/>}>Create</Button></div>}>
        <div className="space-y-3">
          <Input label="Consignor Name" value={form.consignorName} onChange={e => setForm({...form, consignorName: e.target.value})} required />
          <Input label="Contact" value={form.consignorContact} onChange={e => setForm({...form, consignorContact: e.target.value})} />
          <Input label="Address" value={form.consignorAddress} onChange={e => setForm({...form, consignorAddress: e.target.value})} />
          <Input label="Commission %" type="number" value={String(form.commissionPercent)} onChange={e => setForm({...form, commissionPercent: Number(e.target.value)})} />
        </div>
      </Modal>
    </div>
  )
}
